import type { AbaDoPerfil } from "./telas/Perfil"
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  api,
  atualizarContaGuardada,
  contaDaEntrada,
  contaGuardada,
  esquecerSessao,
  guardarCredencial,
  guardarSessao,
  lembrarEntidade,
  type AlcanceDeConfiguracoes,
  type Conta,
} from "./api"
import { mensagemDeRecusa } from "./componentes/BlocoDeCampos"
import {
  ehItemDeConfiguracao,
  gruposDeConfiguracao,
  primeiroItem,
  type ItemDeConfiguracao,
} from "./itens-de-configuracao"
import { niveisDaTrilha } from "./casca/trilha"
import { BuscaGeral } from "./casca/BuscaGeral"
import { Casca } from "./casca/Casca"
import { chaveDoDestino, destinosDaBusca } from "./casca/destinos-da-busca"
import { Toaster } from "./componentes/Toast"
import {
  guardarPosicao,
  posicaoGuardada,
  type PosicaoDaBarra,
} from "./preferencias"
import type { RecorteDaLista } from "./recortes"
import { comoGravarNoHistorico, fichaAbertaDe, itemDoMenu, telaNormalizada, voltarDaFicha } from "./navegacao"
import { caminhoDaEntrada, caminhoParaTela, telaParaCaminho } from "./roteador"
import {
  TELAS,
  secoesDoTrilho,
  type ChaveDeTela,
  type RecursosDaTela,
} from "./registro-de-telas"
import { estadoLimpo, type EstadoDoPainel } from "./estado-do-painel"
import { Autenticidade } from "./telas/Autenticidade"
import { Entrar } from "./telas/Entrar"
import { Esteira } from "./telas/Esteira"
import { PrimeiroAcesso } from "./telas/PrimeiroAcesso"
import {
  ABA_INICIAL,
  Ficha,
  type AbaDaFicha,
  type SubmenuDaFicha,
} from "./telas/Ficha"

export type TelaDeOrigem =
  | { nome: ChaveDeTela }
  | ({ nome: "painel" } & EstadoDoPainel)
  | { nome: "perfil"; aba?: AbaDoPerfil }
  | { nome: "configuracoes"; item?: ItemDeConfiguracao; entidade?: number; perfil?: string; tipo?: string; cargo?: string; sistema?: string }

export type Tela =
  | TelaDeOrigem
  | { nome: "ficha"; contratoId: number; aba: AbaDaFicha; submenu: SubmenuDaFicha; origem?: TelaDeOrigem }
  | {
      nome: "esteira"
      contratoId: number
      pecaId?: number
      abaDaFicha: AbaDaFicha
      submenuDaFicha: SubmenuDaFicha
      origem?: TelaDeOrigem
    }

function codigoDeAutenticidade(): string | null {
  const caminho = window.location.pathname
  const prefixo = "/autenticidade/"
  if (!caminho.startsWith(prefixo)) return null
  const codigo = decodeURIComponent(caminho.slice(prefixo.length)).trim()
  return codigo === "" ? null : codigo
}

export function Aplicacao() {
  const [codigo] = useState<string | null>(() => codigoDeAutenticidade())
  const [conta, definirConta] = useState<Conta | null>(() => contaGuardada())
  const [sessaoCaiu, definirSessaoCaiu] = useState(false)

  useEffect(() => {
    const aoCair = () => {
      definirConta(null)
      definirSessaoCaiu(true)
    }
    window.addEventListener("sessao-expirada", aoCair)
    return () => window.removeEventListener("sessao-expirada", aoCair)
  }, [])

  useEffect(() => {
    if (conta === null || (conta.email !== "" && conta.cpf !== "")) return
    let vivo = true
    api
      .perfil()
      .then((perfil) => {
        if (!vivo) return
        const completa: Conta = { ...conta, email: perfil.email, cpf: perfil.cpf ?? "" }
        atualizarContaGuardada(completa)
        definirConta(completa)
      })
      .catch(() => {
      })
    return () => {
      vivo = false
    }
  }, [conta])
  const [tela, definirTela] = useState<Tela>(
    () => caminhoParaTela(window.location.pathname, window.location.search) ?? { nome: "painel" },
  )
  const [posicao, definirPosicao] = useState<PosicaoDaBarra>(() => posicaoGuardada())

  const telaAnterior = useRef(tela)
  const substituirNoHistorico = useRef(false)
  const semSessao = conta === null
  useEffect(() => {
    const caminhoAtual = window.location.pathname + window.location.search
    if (semSessao) {
      const entrada = caminhoDaEntrada(tela)
      if (entrada !== caminhoAtual) window.history.replaceState(null, "", entrada)
      telaAnterior.current = tela
      return
    }
    const caminhoNovo = telaParaCaminho(tela)
    if (caminhoNovo !== caminhoAtual) {
      const substituir = substituirNoHistorico.current
      substituirNoHistorico.current = false
      if (substituir || comoGravarNoHistorico(telaAnterior.current, tela) === "replace") {
        window.history.replaceState(null, "", caminhoNovo)
      } else {
        window.history.pushState(null, "", caminhoNovo)
      }
    }
    telaAnterior.current = tela
  }, [tela, semSessao])

  useEffect(() => {
    const aoNavegar = () => {
      const nova = caminhoParaTela(window.location.pathname, window.location.search)
      definirTela(nova ?? { nome: "painel" })
    }
    window.addEventListener("popstate", aoNavegar)
    return () => window.removeEventListener("popstate", aoNavegar)
  }, [])

  const [adiouATroca, definirAdiouATroca] = useState(false)

  const [recorte, definirRecorte] = useState<RecorteDaLista | null>(null)

  const [alcance, definirAlcance] = useState<AlcanceDeConfiguracoes | null>(null)
  const [recusaDoAlcance, definirRecusaDoAlcance] = useState<string | null>(null)
  useEffect(() => {
    definirAlcance(null)
    definirRecusaDoAlcance(null)
    if (conta === null) return
    let vivo = true
    api
      .alcanceDeConfiguracoes()
      .then((resposta) => {
        if (vivo) definirAlcance(resposta)
      })
      .catch((falha: unknown) => {
        if (vivo) definirRecusaDoAlcance(mensagemDeRecusa(falha))
      })
    return () => {
      vivo = false
    }
  }, [conta?.usuario_id, conta?.entidade?.id])

  const [siglaDaEntidade, definirSiglaDaEntidade] = useState<string | null>(null)
  const saberDaEntidade = useCallback((sigla: string | null) => definirSiglaDaEntidade(sigla), [])

  const grupos = useMemo(() => (alcance === null ? null : gruposDeConfiguracao(alcance)), [alcance])
  const secoes = useMemo(() => secoesDoTrilho(grupos), [grupos])

  useEffect(() => {
    definirTela((atual) => telaNormalizada(atual, grupos))
  }, [grupos, tela])

  const [processoDoContrato, definirProcessoDoContrato] = useState<string | null>(
    null,
  )
  const saberDoProcesso = useCallback(
    (valor: string | null) => definirProcessoDoContrato(valor),
    [],
  )

  const trocarPosicao = (nova: PosicaoDaBarra) => {
    guardarPosicao(nova)
    definirPosicao(nova)
  }

  if (codigo !== null) {
    return <Autenticidade codigo={codigo} />
  }

  if (conta === null) {
    return (
      <Entrar
        aoEntrar={(nova) => {
          definirSessaoCaiu(false)
          definirAdiouATroca(false)
          definirConta(nova)
        }}
        aviso={
          sessaoCaiu
            ? "Sua sessão terminou. Entre de novo."
            : undefined
        }
      />
    )
  }

  const aoTrocarSenha = (credencial: string, perfil: { senha_provisoria: boolean; senha_alterada_em: string | null }) => {
    guardarCredencial(credencial)
    const atualizada: Conta = {
      ...conta,
      senha_provisoria: perfil.senha_provisoria,
      senha_alterada_em: perfil.senha_alterada_em,
      troca_de_senha_exigida: false,
      troca_de_senha_bloqueia: false,
    }
    atualizarContaGuardada(atualizada)
    definirConta(atualizada)
  }

  const aoMudarDadosDaConta = (perfil: { nome: string; email: string }) => {
    const atualizada: Conta = { ...conta, nome: perfil.nome, email: perfil.email }
    atualizarContaGuardada(atualizada)
    definirConta(atualizada)
  }

  if (conta.troca_de_senha_exigida && !adiouATroca) {
    return (
      <PrimeiroAcesso
        conta={conta}
        aoTrocar={aoTrocarSenha}
        aoAdiar={() => definirAdiouATroca(true)}
      />
    )
  }

  const sair = () => {
    esquecerSessao()
    definirConta(null)
    definirRecorte(null)
    definirAdiouATroca(false)
    definirTela({ nome: "painel" })
  }

  const trocarEntidade = async (entidadeId: number) => {
    const resposta = await api.trocarEntidade(entidadeId)
    const nova = contaDaEntrada(resposta)
    guardarSessao(resposta.credencial, nova)
    lembrarEntidade(nova.usuario_id, entidadeId)
    definirRecorte(null)
    definirProcessoDoContrato(null)
    definirTela((atual) => (atual.nome === "ficha" || atual.nome === "esteira" ? { nome: "contratos" } : atual))
    definirConta(nova)
  }

  const abrirContrato = (id: number, aba: AbaDaFicha = ABA_INICIAL) => {
    definirProcessoDoContrato(null)
    definirTela((atual) => fichaAbertaDe(atual, id, aba))
  }

  const abrirLista = (novo: RecorteDaLista | null) => {
    definirRecorte(novo)
    definirTela({ nome: "contratos" })
  }

  const irPara = (chave: ChaveDeTela) => {
    definirRecorte(null)
    if (chave === "configuracoes") {
      const item = grupos === null ? undefined : primeiroItem(grupos)
      definirTela(item === undefined ? { nome: chave } : { nome: chave, item })
      return
    }
    definirTela({ nome: chave })
  }

  const abrirConfiguracao = (item: ItemDeConfiguracao) => {
    definirRecorte(null)
    definirTela({ nome: "configuracoes", item })
  }

  const abrirEntidade = (id: number | null, substituir = false) => {
    substituirNoHistorico.current = substituir
    definirSiglaDaEntidade(null)
    definirTela(id === null ? { nome: "configuracoes", item: "entidades" } : { nome: "configuracoes", item: "entidades", entidade: id })
  }

  const abrirConfiguracaoFiltrada = (
    item: ItemDeConfiguracao,
    filtros: { perfil?: string | null; cargo?: string | null; tipo?: string | null; sistema?: string | null },
    substituir = false,
  ) => {
    substituirNoHistorico.current = substituir
    definirTela((atual) => {
      const antes = atual.nome === "configuracoes" && "item" in atual && atual.item === item ? (atual as Record<string, unknown>) : {}
      const nova: Record<string, unknown> = { nome: "configuracoes", item }
      for (const chave of ["perfil", "cargo", "tipo", "sistema"] as const) {
        const valor = chave in filtros ? filtros[chave] : antes[chave]
        if (typeof valor === "string" && valor !== "") nova[chave] = valor
      }
      return nova as Tela
    })
  }

  const abrirContasDoPerfil = (perfil: string | null, substituir = false) =>
    abrirConfiguracaoFiltrada("contas", { perfil }, substituir)

  const abrirGrupoDeConfiguracao = (area: string) => {
    const item = grupos?.find((g) => g.area === area)?.itens[0]
    if (item !== undefined) abrirConfiguracao(item)
  }

  const recursos: RecursosDaTela = {
    abrirContrato,
    abrirLista,
    recorte,
    abrirPerfil: (aba) => {
      definirRecorte(null)
      definirTela(aba === undefined || aba === "dados" ? { nome: "perfil" } : { nome: "perfil", aba })
    },
    abaDoPerfil: tela.nome === "perfil" && "aba" in tela ? tela.aba : undefined,
    posicao,
    aoTrocarPosicao: trocarPosicao,
    aoTrocarSenha,
    aoMudarDadosDaConta,
    itemDeConfiguracao: tela.nome === "configuracoes" && "item" in tela ? tela.item : undefined,
    alcance,
    recusaDoAlcance,
    abrirConfiguracao,
    entidadeDeConfiguracao: tela.nome === "configuracoes" && "entidade" in tela ? tela.entidade : undefined,
    abrirEntidade,
    aoSaberDaEntidade: saberDaEntidade,
    perfilDeContas: tela.nome === "configuracoes" && "perfil" in tela ? tela.perfil : undefined,
    abrirContasDoPerfil,
    tipoDoFiltro: tela.nome === "configuracoes" && "tipo" in tela ? tela.tipo : undefined,
    cargoDoFiltro: tela.nome === "configuracoes" && "cargo" in tela ? tela.cargo : undefined,
    sistemaDoFiltro: tela.nome === "configuracoes" && "sistema" in tela ? tela.sistema : undefined,
    abrirConfiguracaoFiltrada,
    estadoDoPainel: tela.nome === "painel" ? estadoLimpo(tela as EstadoDoPainel) : {},
    aoMudarPainel: (estado) => definirTela({ nome: "painel", ...estadoLimpo(estado) }),
  }

  const destinos = destinosDaBusca(secoes, grupos, { irPara, abrirConfiguracao, abrirPerfil: recursos.abrirPerfil })
  const trilha = niveisDaTrilha(tela, processoDoContrato, definirTela, grupos, siglaDaEntidade)
  const itemAberto = tela.nome === "configuracoes" && "item" in tela ? tela.item : undefined
  const subitemAtual =
    itemAberto === undefined ? null : (grupos?.find((g) => g.itens.includes(itemAberto))?.area ?? null)

  return (
    <Casca
      secoes={secoes}
      atual={itemDoMenu(tela)}
      aoTrocar={irPara}
      subitemAtual={subitemAtual}
      itemDoSubitemAtual={itemAberto ?? null}
      aoTrocarSubitem={(_chave, area, item) =>
        item !== undefined && ehItemDeConfiguracao(item) ? abrirConfiguracao(item) : abrirGrupoDeConfiguracao(area)
      }
      aoAbrirAvisos={() => irPara("avisos")}
      aoAbrirContratoDoAviso={(id) => abrirContrato(id)}
      aoAbrirPerfil={recursos.abrirPerfil}
      trilha={trilha}
      conta={conta}
      aoSair={sair}
      aoTrocarEntidade={trocarEntidade}
      posicao={posicao}
      busca={
        <BuscaGeral
          destinos={destinos}
          atual={chaveDoDestino(tela)}
          usuarioId={conta.usuario_id}
          aoAbrirContrato={(id) => abrirContrato(id)}
          aoVerTodos={(termo) => abrirLista({ rotulo: `Busca: “${termo}”`, parametros: { busca: termo } })}
        />
      }
    >
      <Fragment key={conta.entidade?.id ?? "plataforma"}>
      {tela.nome !== "ficha" &&
        tela.nome !== "esteira" &&
        TELAS[tela.nome].desenhar(recursos)}

      {tela.nome === "ficha" && (
        <Ficha
          contratoId={tela.contratoId}
          aba={tela.aba}
          submenu={tela.submenu}
          aoTrocarAba={(aba) =>
            definirTela((anterior) =>
              anterior.nome === "ficha" ? { ...anterior, aba } : anterior,
            )
          }
          aoTrocarSubmenu={(qual, item) =>
            definirTela((anterior) =>
              anterior.nome === "ficha"
                ? { ...anterior, submenu: { ...anterior.submenu, [qual]: item } }
                : anterior,
            )
          }
          aoSaberDoProcesso={saberDoProcesso}
          aoVoltar={() => definirTela(voltarDaFicha(tela))}
          aoAbrirProcesso={(pecaId) =>
            definirTela({
              nome: "esteira",
              contratoId: tela.contratoId,
              pecaId,
              abaDaFicha: tela.aba,
              submenuDaFicha: tela.submenu,
              ...(tela.origem === undefined ? {} : { origem: tela.origem }),
            })
          }
        />
      )}
      {tela.nome === "esteira" && (
        <Esteira
          contratoId={tela.contratoId}
          cpfNaSessao={conta.cpf}
          pecaId={tela.pecaId}
          aoSaberDoProcesso={saberDoProcesso}
        />
      )}
      </Fragment>
      <Toaster />
    </Casca>
  )
}
